import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthContext } from '../hooks/AuthContext';
import useAdminAutoRefresh from './useAdminAutoRefresh';
import './AdminDashboard.css';

const AUTO_REFRESH_INTERVAL_MS = 20000;

const RANGE_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
];

function formatInteger(value) {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(value) || 0);
}

function formatCurrency(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);
}

function formatDateTime(value) {
  if (!value) {
    return 'Unknown';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));
}

function formatDuration(seconds) {
  if (!seconds) {
    return '0m';
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
}

const MetricCard = ({ title, value, helper, linkTo, linkLabel = 'View details' }) => (
  <article className="cq-admin-metric-card">
    <p className="cq-admin-metric-card__title">{title}</p>
    <p className="cq-admin-metric-card__value">{value}</p>
    {helper ? <p className="cq-admin-metric-card__helper">{helper}</p> : null}
    {linkTo ? <Link className="cq-admin-card-link" to={linkTo}>{linkLabel}</Link> : null}
  </article>
);

const SummaryCard = ({ title, linkTo, linkLabel, children }) => (
  <article className="cq-admin-summary-card">
    <div className="cq-admin-summary-card__header">
      <h3>{title}</h3>
      {linkTo ? <Link className="cq-admin-card-link" to={linkTo}>{linkLabel}</Link> : null}
    </div>
    {children}
  </article>
);

const AdminDashboard = () => {
  const { authenticatedUser } = useAuthContext();
  const [range, setRange] = useState('today');
  const [state, setState] = useState({
    loading: true,
    refreshing: false,
    error: '',
    data: null,
    lastUpdatedAt: null,
  });
  const { refreshNonce, triggerRefresh } = useAdminAutoRefresh({
    enabled: Boolean(authenticatedUser?.token),
    intervalMs: AUTO_REFRESH_INTERVAL_MS,
  });

  useEffect(() => {
    if (!authenticatedUser?.token) {
      return undefined;
    }

    const controller = new AbortController();

    const loadDashboard = async ({ background = false } = {}) => {
      setState((current) => ({
        ...current,
        loading: background ? current.loading : true,
        refreshing: background,
        error: '',
      }));

      try {
        const response = await fetch('/.netlify/functions/admin-get-dashboard-summary', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authenticatedUser.token}`,
          },
          body: JSON.stringify({ range }),
          signal: controller.signal,
        });

        const payload = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(payload?.message || 'Unable to load dashboard summary.');
        }

        setState({
          loading: false,
          refreshing: false,
          error: '',
          data: payload,
          lastUpdatedAt: new Date().toISOString(),
        });
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }

        setState({
          loading: false,
          refreshing: false,
          error: error.message || 'Unable to load dashboard summary.',
          data: null,
          lastUpdatedAt: null,
        });
      }
    };

    loadDashboard();

    return () => controller.abort();
  }, [authenticatedUser, range, refreshNonce]);

  const dashboard = state.data;
  const hasActivity = dashboard && (
    dashboard.activity.games_started
    || dashboard.activity.games_completed
    || dashboard.activity.sessions_failed
    || dashboard.activity.new_users
    || dashboard.activity.active_users
  );

  return (
    <section className="cq-admin-page">
      <header className="cq-admin-page__header">
        <div>
          <p className="cq-admin-page__eyebrow">Read-Only MVP</p>
          <h2 className="cq-admin-page__title">Admin Dashboard</h2>
          <p className="cq-admin-page__copy">
            Is Cringe Quest healthy, active, and operating as expected?
          </p>
        </div>

        <div className="cq-admin-page__actions">
          <div className="cq-admin-page__actions-row cq-admin-range-filter" role="tablist" aria-label="Dashboard range">
            {RANGE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`cq-admin-range-filter__button${range === option.value ? ' is-active' : ''}`}
                onClick={() => setRange(option.value)}
              >
                {option.label}
              </button>
            ))}
            <button
              type="button"
              className="cq-admin-action-button cq-admin-action-button--secondary"
              disabled={state.loading || state.refreshing}
              onClick={triggerRefresh}
            >
              Refresh
            </button>
          </div>
          <p className="cq-admin-page__meta">
            {state.refreshing ? 'Refreshing...' : `Last updated ${formatDateTime(state.lastUpdatedAt)}`}
          </p>
        </div>
      </header>

      {state.loading ? (
        <div className="cq-admin-empty-state">
          <p className="cq-admin-empty-state__title">Loading admin dashboard...</p>
        </div>
      ) : null}

      {!state.loading && state.error ? (
        <div className="cq-admin-empty-state">
          <p className="cq-admin-empty-state__title">Unable to load dashboard summary.</p>
          <p className="cq-admin-empty-state__copy">{state.error}</p>
        </div>
      ) : null}

      {!state.loading && !state.error && dashboard ? (
        <>
          {!hasActivity ? (
            <div className="cq-admin-empty-state">
              <p className="cq-admin-empty-state__title">No activity found for this period.</p>
            </div>
          ) : null}

          <div className="cq-admin-metrics-grid">
            <MetricCard
              title="Games Started"
              value={formatInteger(dashboard.activity.games_started)}
              helper="Sessions created in range"
              linkTo="/admin/sessions"
            />
            <MetricCard
              title="Games Completed"
              value={formatInteger(dashboard.activity.games_completed)}
              helper="Sessions marked completed"
              linkTo="/admin/sessions"
            />
            <MetricCard
              title="Active Users"
              value={formatInteger(dashboard.activity.active_users)}
              helper="Users who started at least one session"
              linkTo="/admin/sessions"
            />
            <MetricCard
              title="Estimated AI Cost"
              value={formatCurrency(dashboard.usage.estimated_ai_cost)}
              helper={`Avg completed game: ${formatCurrency(dashboard.usage.average_cost_per_completed_game)}`}
              linkTo="/admin/analytics"
            />
            <MetricCard
              title="Credits Purchased"
              value={formatInteger(dashboard.credits.credits_purchased)}
              helper={`Revenue: ${formatCurrency(dashboard.credits.revenue_amount)}`}
              linkTo="/admin/payments"
            />
            <MetricCard
              title="Credits Consumed"
              value={formatInteger(dashboard.credits.credits_consumed)}
              helper={`Outstanding: ${formatInteger(dashboard.credits.outstanding_credits)}`}
              linkTo="/admin/payments"
            />
            <MetricCard
              title="Revenue"
              value={formatCurrency(dashboard.credits.revenue_amount)}
              helper={`${formatInteger(dashboard.credits.failed_payments)} failed payments`}
              linkTo="/admin/payments"
            />
            <MetricCard
              title="Failed Sessions"
              value={formatInteger(dashboard.activity.sessions_failed)}
              helper={`${formatInteger(dashboard.activity.new_users)} new users`}
              linkTo="/admin/sessions"
            />
          </div>

          <div className="cq-admin-content-grid">
            <SummaryCard title="Content Snapshot" linkTo="/admin/content" linkLabel="View Content Library">
              <ul className="cq-admin-summary-list">
                <li>Active Game Packs: {formatInteger(dashboard.content.active_game_packs)}</li>
                <li>Active Quest Packs: {formatInteger(dashboard.content.active_quest_packs)}</li>
                <li>Total Questions / Prompts: {formatInteger(dashboard.content.total_questions)}</li>
              </ul>

              {dashboard.content.recently_added?.length ? (
                <div className="cq-admin-summary-subsection">
                  <p className="cq-admin-summary-subsection__title">Recently Added Content</p>
                  <ul className="cq-admin-compact-list">
                    {dashboard.content.recently_added.map((item) => (
                      <li key={`${item.entity_type}-${item.id}`}>
                        <span>{item.label}</span>
                        <span>{formatDateTime(item.created_at)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </SummaryCard>

            <SummaryCard title="Creator Studio" linkTo="/admin/creator-studio" linkLabel="Open Creator Studio">
              <ul className="cq-admin-summary-list">
                <li>Status: {dashboard.content.creator_studio_active ? 'Active' : 'Inactive'}</li>
                <li>Admin Only: {dashboard.content.creator_studio_admin_only ? 'Yes' : 'No'}</li>
                <li>Host Persona: {dashboard.content.creator_studio_host_name || 'Unknown'}</li>
                <li>Last Updated: {formatDateTime(dashboard.content.creator_studio_last_updated_at)}</li>
              </ul>
            </SummaryCard>

            <SummaryCard
              title="Operational Alerts"
              linkTo={dashboard.operational_alerts.length ? (dashboard.operational_alerts[0].link_to || '/admin/sessions') : '/admin/sessions'}
              linkLabel="Review Sessions"
            >
              {dashboard.operational_alerts.length ? (
                <ul className="cq-admin-alert-list">
                  {dashboard.operational_alerts.map((alert) => (
                    <li key={alert.type} className={`cq-admin-alert cq-admin-alert--${alert.severity}`}>
                      <p className="cq-admin-alert__type">{alert.type.replace(/_/g, ' ')}</p>
                      <p className="cq-admin-alert__message">{alert.message}</p>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="cq-admin-empty-inline">No operational alerts found for the selected period.</div>
              )}
            </SummaryCard>
          </div>

          <div className="cq-admin-bottom-grid">
            <SummaryCard title="Usage & Cost" linkTo="/admin/analytics" linkLabel="Open Analytics">
              <ul className="cq-admin-summary-list">
                <li>Total Tokens: {formatInteger(dashboard.usage.total_tokens)}</li>
                <li>Estimated AI Cost: {formatCurrency(dashboard.usage.estimated_ai_cost)}</li>
                <li>Average Session Duration: {formatDuration(dashboard.usage.average_session_duration_seconds)}</li>
                <li>
                  Highest-Cost Session:{' '}
                  {dashboard.usage.highest_cost_session_id
                    ? `${formatCurrency(dashboard.usage.highest_cost_session_amount)}`
                    : 'None'}
                </li>
              </ul>
            </SummaryCard>

            <SummaryCard title="Recent Sessions" linkTo="/admin/sessions" linkLabel="View Sessions">
              {dashboard.recent_sessions.length ? (
                <ul className="cq-admin-compact-list">
                  {dashboard.recent_sessions.map((session) => (
                    <li key={session.id}>
                      <span>
                        {session.game_pack_name || 'Unknown Pack'}
                        {session.quest_pack_name ? ` / ${session.quest_pack_name}` : ''}
                      </span>
                      <span>{session.status} • {formatDateTime(session.created_at)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="cq-admin-empty-inline">No recent sessions found for the selected period.</div>
              )}
            </SummaryCard>

            <SummaryCard title="Revenue Snapshot" linkTo="/admin/payments" linkLabel="View Payments">
              {dashboard.credits.recent_purchases.length ? (
                <ul className="cq-admin-compact-list">
                  {dashboard.credits.recent_purchases.map((purchase) => (
                    <li key={purchase.id}>
                      <span>{purchase.purchase_kind}</span>
                      <span>{formatCurrency(purchase.amount)} • {formatDateTime(purchase.created_at)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="cq-admin-empty-inline">No successful purchases found for the selected period.</div>
              )}
            </SummaryCard>
          </div>
        </>
      ) : null}
    </section>
  );
};

export default AdminDashboard;
